import React, { useEffect, useRef } from 'react';
import { Empty, Tooltip, Typography } from '@duxton-web/ui';

import ConstraintsViewer, {
  ConstraintsViewerType,
} from '@/common/components/ConstraintsViewer/ConstraintsViewer';
import { ChangelogState } from '@/common/const';
import { VariableType } from '@/constants';
import JsonDiffViewer from '@/features/json-diff-viewer';
import { Rollout, RolloutTarget, VariableInfo } from '@/lib/PlannerDTOs';
import {
  DIFF_SHOWFOR,
  DiffShowFor_Keys,
} from '@/pages/RolloutDifference/RolloutDiffViewer/RolloutDiffUtils/RolloutDiffUtils';
import {
  extractInteger,
  getKeyFromValue,
  marks,
} from '@/pages/RolloutTab/RolloutEditor/RolloutTrackConf/RolloutTrackConf';
import { getRolloutTargetIndex } from '@/utils/rollout';

interface RolloutTargetWrapperProps {
  rollout: Rollout;
  rolloutTarget: RolloutTarget;
  rolloutToCompare: Rollout;
  rolloutTargetToCompare: RolloutTarget;
  position: number;
  showFor: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys];
  changelog: any;
  variable: VariableInfo;
}

const RollouTargetWrapper = ({
  rollout,
  rolloutTarget,
  rolloutToCompare,
  rolloutTargetToCompare,
  position,
  showFor = DIFF_SHOWFOR.ALL,
  changelog,
  variable,
}: RolloutTargetWrapperProps) => {
  if (!rolloutTarget && !rolloutTargetToCompare) {
    const rolloutLimit = rollout?.trackConf?.limit;

    if (!rolloutLimit)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            className={`rollout-target-value-${
              showFor === DIFF_SHOWFOR.BEFORE ? 'deleted' : 'added'
            }`}
          >
            &nbsp;
          </div>
        </div>
      );

    const sampleRatePercentage = rolloutLimit
      ? Math.round((1 - rolloutLimit) * 100)
      : 100;
    const toDisplay = extractInteger(
      marks[getKeyFromValue(marks, sampleRatePercentage)!],
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          className={`rollout-target-value-${
            showFor === DIFF_SHOWFOR.BEFORE ? 'deleted' : 'added'
          }`}
        >
          <Typography.Text strong>%</Typography.Text>
          <Tooltip title={toDisplay}>
            <Typography.Text>{toDisplay}</Typography.Text>
          </Tooltip>
        </div>
      </div>
    );
  }

  if (!rolloutTarget)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );

  return (
    <ConstraintsViewer
      rollout={rollout}
      rolloutTarget={rolloutTarget}
      rolloutToCompare={rolloutToCompare}
      rolloutTargetToCompare={rolloutTargetToCompare}
      showFor={showFor}
      variableType={changelog?.type || variable?.type}
      domain={changelog?.domain || variable?.domain}
      position={position}
      type={ConstraintsViewerType.ROLLOUT}
      showValue={showFor === DIFF_SHOWFOR.ALL}
    />
  );
};

interface JsonDiffProps {
  value: any;
  changelog: any;
  variable: VariableInfo;
}

const ChangelogTargetDiffWrapper = ({
  value,
  changelog,
  variable,
}: JsonDiffProps) => {
  function getPreviousData() {
    return {
      rollout: value?.before?.rollout || null,
      rolloutTarget: value?.before?.rolloutTarget || null,
      index: value?.before?.index || -1,
    };
  }

  function getCurrentData() {
    return {
      rollout: value?.after?.rollout || null,
      rolloutTarget: value?.after?.rolloutTarget || null,
      index: value?.after?.index || -1,
    };
  }

  const { rollout: previousRollout, rolloutTarget: previousRolloutTarget } =
    getPreviousData();
  const { rollout: currentRollout, rolloutTarget: currentRolloutTarget } =
    getCurrentData();

  if (value.state === ChangelogState.NONE)
    return (
      <div
        data-testid="changelog-item-unchanged-rollout-target-container"
        className="changelog-item-unchanged-rollout-target-container"
        style={{ width: '100%' }}
      >
        <RollouTargetWrapper
          rollout={previousRollout}
          rolloutTarget={previousRolloutTarget}
          rolloutToCompare={currentRollout}
          rolloutTargetToCompare={currentRolloutTarget}
          position={getRolloutTargetIndex(
            currentRolloutTarget,
            currentRollout?.rollouts,
          )}
          showFor={DIFF_SHOWFOR.ALL}
          changelog={changelog}
          variable={variable}
        />
      </div>
    );

  const previousRolloutTargetIndex = getRolloutTargetIndex(
    previousRolloutTarget,
    previousRollout?.rollouts,
  );
  const currentRolloutTargetIndex = getRolloutTargetIndex(
    currentRolloutTarget,
    currentRollout?.rollouts,
  );

  const showBeforeAndAfterConstraintsViewer =
    ((previousRolloutTargetIndex > -1 || currentRolloutTargetIndex > -1) &&
      variable?.type == VariableType.JSON) ||
    variable?.type !== VariableType.JSON;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showBeforeAndAfterConstraintsViewer && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="changelog-item-rollout-targets-diff-wrapper">
            <div className="changelog-item-before-rollout-targets-diff-title">
              BEFORE
            </div>
            <div
              data-testid="changelog-item-before-rollout-target-container"
              className="changelog-item-before-rollout-target-container"
            >
              <RollouTargetWrapper
                rollout={previousRollout}
                rolloutTarget={previousRolloutTarget}
                rolloutToCompare={currentRollout}
                rolloutTargetToCompare={currentRolloutTarget}
                position={previousRolloutTargetIndex}
                showFor={DIFF_SHOWFOR.BEFORE}
                changelog={changelog}
                variable={variable}
              />
            </div>
          </div>

          <div className="changelog-item-rollout-targets-diff-wrapper">
            <div className="changelog-item-after-rollout-targets-diff-title">
              AFTER
            </div>
            <div
              data-testid="changelog-item-after-rollout-target-container"
              className="changelog-item-after-rollout-target-container"
            >
              <RollouTargetWrapper
                rollout={currentRollout}
                rolloutTarget={currentRolloutTarget}
                rolloutToCompare={previousRollout}
                rolloutTargetToCompare={previousRolloutTarget}
                position={currentRolloutTargetIndex}
                showFor={DIFF_SHOWFOR.AFTER}
                changelog={changelog}
                variable={variable}
              />
            </div>
          </div>
        </div>
      )}

      <JsonDiffViewer
        value={value}
        oldValue={previousRolloutTarget?.string}
        newValue={currentRolloutTarget?.string}
      />
    </div>
  );
};

const JsonDiff = ({ value, changelog, variable }: JsonDiffProps) => {
  const changelogTargetDiffRef = useRef(null);

  const resizeObserver = new ResizeObserver((entries) => {
    if (!changelogTargetDiffRef.current) return;

    if (entries?.[0].target === changelogTargetDiffRef.current) {
      const parentHeight = (
        entries?.[0].target.children.item(0).children.item(0) as HTMLElement
      )?.offsetHeight;

      changelogTargetDiffRef.current
        .getElementsByClassName(
          'changelog-item-before-rollout-target-container',
        )?.[0]
        ?.setAttribute(
          'style',
          `height: calc(${parentHeight}px); max-height: 460px;`,
        );
      changelogTargetDiffRef.current
        .getElementsByClassName(
          'changelog-item-after-rollout-target-container',
        )?.[0]
        ?.setAttribute(
          'style',
          `height: calc(${parentHeight}px); max-height: 460px;`,
        );

      resizeObserver.disconnect();
    }
  });

  useEffect(() => {
    resizeObserver.observe(changelogTargetDiffRef.current);
  }, []);

  return (
    <div ref={changelogTargetDiffRef}>
      <ChangelogTargetDiffWrapper
        value={value}
        changelog={changelog}
        variable={variable}
      />
    </div>
  );
};

export default JsonDiff;
